// «Поделиться» на профиле /tutors/<slug>: кнопка → окно с готовой картинкой
// 1080×1350 (PNG), «Скачать картинку», «Копировать ссылку» и, на телефоне с
// Web Share API, «Отправить» (картинка файлом, если браузер умеет, иначе ссылка).
// Картинку рисует сервер (SVG, /api/map/share/<slug>), PNG — share-raster.ts.
import { useEffect, useRef, useState } from "react";

import { shareCardPng } from "./share-raster";
import type { SiteLang } from "@/components/site/SiteChrome";
import { SHARE_COPY, shareCardPath } from "@/lib/partner-share-card";
import { profileUrl } from "./partner-jsonld";

type State = { kind: "idle" } | { kind: "loading" } | { kind: "ready"; blob: Blob; url: string } | { kind: "error" };

/** Запасной путь копирования, когда navigator.clipboard нет (http, старый браузер). */
function legacyCopy(text: string): boolean {
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

export function ShareBlock({ slug, name, lang }: { slug: string; name: string; lang: SiteLang }) {
  const t = SHARE_COPY[lang];
  const link = profileUrl(lang, slug);
  const [open, setOpen] = useState(false);
  const [img, setImg] = useState<State>({ kind: "idle" });
  const [copy, setCopy] = useState<"idle" | "done" | "failed">("idle");
  const [canShare, setCanShare] = useState(false);
  const opener = useRef<HTMLButtonElement>(null);
  const closer = useRef<HTMLButtonElement>(null);
  const fileName = `yolutap-${slug}.png`;

  useEffect(() => setCanShare(typeof navigator !== "undefined" && typeof navigator.share === "function"), []);

  // Картинка готовится при первом открытии и живёт, пока открыта страница.
  useEffect(() => {
    if (!open || img.kind !== "idle") return;
    setImg({ kind: "loading" });
    shareCardPng(shareCardPath(slug, lang))
      .then((blob) => setImg({ kind: "ready", blob, url: URL.createObjectURL(blob) }))
      .catch(() => setImg({ kind: "error" }));
  }, [open, img.kind, slug, lang]);
  useEffect(() => () => void (img.kind === "ready" && URL.revokeObjectURL(img.url)), [img]);

  useEffect(() => {
    if (!open) return;
    const back = opener.current;
    closer.current?.focus();
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      back?.focus();
    };
  }, [open]);

  const copyLink = async () => {
    let ok = false;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(link);
        ok = true;
      }
    } catch {
      ok = false;
    }
    if (!ok) ok = legacyCopy(link);
    setCopy(ok ? "done" : "failed");
  };

  const send = async () => {
    try {
      const file = img.kind === "ready" ? new File([img.blob], fileName, { type: "image/png" }) : null;
      if (file && navigator.canShare?.({ files: [file] })) await navigator.share({ files: [file], title: name, text: `${t.tagline}: ${link}` });
      else await navigator.share({ title: name, text: t.tagline, url: link });
    } catch {
      /* человек закрыл окно «Поделиться» — не ошибка */
    }
  };

  return (
    <>
      <button ref={opener} type="button" className="hp-btn hp-btn--outline tm-btn tm-share-btn" onClick={() => setOpen(true)}>
        {t.button}
      </button>
      {open && (
        <div className="tm-share" role="dialog" aria-modal="true" aria-label={t.title} onClick={() => setOpen(false)}>
          <div className="tm-share__box" onClick={(e) => e.stopPropagation()}>
            <div className="tm-share__head">
              <b>{t.title}</b>
              <button ref={closer} type="button" className="tm-share__close" aria-label={t.close} onClick={() => setOpen(false)}>
                ×
              </button>
            </div>
            <div className="tm-share__preview">
              {img.kind === "ready" ? (
                <img src={img.url} alt={`${name} · ${t.tagline}`} width={1080} height={1350} />
              ) : (
                <p className="tm-muted">{img.kind === "error" ? t.error : t.preparing}</p>
              )}
            </div>
            <p className="tm-muted tm-share__hint">{t.hint}</p>
            <div className="tm-share__actions">
              {img.kind === "ready" && (
                <a className="hp-btn tm-btn" href={img.url} download={fileName}>
                  {t.download}
                </a>
              )}
              <button type="button" className="hp-btn hp-btn--outline tm-btn" onClick={copyLink}>
                {copy === "done" ? t.copied : t.copy}
              </button>
              {canShare && (
                <button type="button" className="hp-btn hp-btn--outline tm-btn" onClick={send}>
                  {t.send}
                </button>
              )}
            </div>
            {copy === "failed" && (
              <label className="tm-share__fallback">
                <span className="tm-muted">{t.copyFailed}</span>
                <input readOnly value={link} onFocus={(e) => e.currentTarget.select()} />
              </label>
            )}
          </div>
        </div>
      )}
    </>
  );
}
